import React from 'react';
import { Eye, ListFilter } from 'lucide-react';
import type { Campaign, CampaignItem, Disparo } from '../../types';
import {
  campaignDispatchProgressPercent,
  campaignOriginLabels,
  campaignTotalLeadCount,
  formatDisparoSchedule,
} from '../../utils/campaignUi';
import './CampaignExecutionCard.css';

export type ExecutionColumnKind = 'pendente' | 'disparado' | 'finalizado';

export interface CampaignExecutionCardProps {
  campaignId: string;
  camp: Campaign | undefined;
  columnItems: CampaignItem[];
  allActiveCampaignItems: CampaignItem[];
  columnKind: ExecutionColumnKind;
  disparo: Disparo | undefined;
  contaLabel: string;
  responsavelLabel: string;
  onOpenListModal: () => void;
  onOpenDetailDrawer: () => void;
  disparoControls: React.ReactNode;
}

export const CampaignExecutionCard: React.FC<CampaignExecutionCardProps> = ({
  campaignId,
  camp,
  columnItems,
  allActiveCampaignItems,
  columnKind,
  disparo,
  contaLabel,
  responsavelLabel,
  onOpenListModal,
  onOpenDetailDrawer,
  disparoControls,
}) => {
  const pills = campaignOriginLabels(camp?.origem);
  const totalLeads = campaignTotalLeadCount(camp, allActiveCampaignItems.length);
  // Progresso em "disparado" agora prefere `disparosRealizados / campaignItemCount`
  // — fonte autoritativa no backend e disponível sem pré-carregar items.
  // Fallback: cálculo histórico baseado em items (pendente + disparado), só
  // útil quando o drawer/modal já carregou items pra essa campanha.
  const progressFromDisparo =
    disparo && typeof disparo.disparosRealizados === 'number' && camp?.campaignItemCount
      ? Math.min(
          100,
          Math.round((100 * disparo.disparosRealizados) / camp.campaignItemCount)
        )
      : null;
  const progress =
    columnKind === 'finalizado'
      ? columnItems.length > 0
        ? 100
        : 0
      : columnKind === 'disparado'
        ? progressFromDisparo ?? campaignDispatchProgressPercent(allActiveCampaignItems)
        : 0;
  const showProgress = columnKind === 'disparado' || columnKind === 'finalizado';

  return (
    <div className="campaign-exec-card" data-campaign-id={campaignId}>
      <div className="campaign-exec-card-body">
        <strong className="campaign-exec-card-title">{camp?.nome || 'Campanha'}</strong>
        <p className="campaign-exec-card-leads-line">
          {totalLeads} {totalLeads === 1 ? 'lead' : 'leads'}
        </p>
        {pills.length > 0 && (
          <div className="campaign-exec-card-pills" aria-label="Fontes">
            {pills.map((p) => (
              <span key={p} className="campaign-exec-card-pill">
                {p}
              </span>
            ))}
          </div>
        )}
        <dl className="campaign-exec-card-meta">
          <div>
            <dt>Conta</dt>
            <dd>{contaLabel}</dd>
          </div>
          <div>
            <dt>Horário</dt>
            <dd>{formatDisparoSchedule(disparo)}</dd>
          </div>
          <div>
            <dt>Responsáveis</dt>
            <dd>{responsavelLabel}</dd>
          </div>
        </dl>
      </div>

      {showProgress && (
        <div className="campaign-exec-card-progress" aria-label="Progresso">
          <span className="campaign-exec-card-progress-label">Progresso</span>
          <div className="campaign-exec-card-progress-row">
            <div className="campaign-exec-card-progress-track">
              <div className="campaign-exec-card-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="campaign-exec-card-progress-pct">{progress}%</span>
          </div>
        </div>
      )}

      <footer className="campaign-exec-card-footer">
        <div className="campaign-exec-card-footer-main">{disparoControls}</div>
        <div className="campaign-exec-card-footer-icons">
          <button
            type="button"
            className="campaign-exec-card-icon-btn"
            onClick={onOpenDetailDrawer}
            aria-label="Ver detalhes da campanha"
            title="Detalhes"
          >
            <Eye size={18} strokeWidth={2} aria-hidden />
          </button>
          <button
            type="button"
            className="campaign-exec-card-icon-btn"
            onClick={onOpenListModal}
            aria-label="Filtrar e agir nos itens"
            title="Lista e filtros"
          >
            <ListFilter size={18} strokeWidth={2} aria-hidden />
          </button>
        </div>
      </footer>
    </div>
  );
};
